// Robust CSV utils: parse, header mapping, row normalization
// Handles BOM, quotes, CRLF, header synonyms, and currency-like numbers.

function stripBOM(s = "") {
  return s && s.charCodeAt(0) === 0xFEFF ? s.slice(1) : s;
}

export function parseCsv(text = "") {
  const src = stripBOM(String(text || ""));
  const rows = [];
  let i = 0, cell = "", cur = [], inQ = false;

  const pushCell = () => { cur.push(cell); cell = ""; };
  const pushRow  = () => { rows.push(cur); cur = []; };

  while (i < src.length) {
    const ch = src[i++];
    if (inQ) {
      if (ch === '"') {
        if (src[i] === '"') { cell += '"'; i++; } else { inQ = false; }
      } else {
        cell += ch;
      }
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ",") pushCell();
      else if (ch === "\n") { pushCell(); pushRow(); }
      else if (ch === "\r") { /* ignore; CRLF handled by \n */ }
      else cell += ch;
    }
  }
  pushCell(); pushRow();

  if (rows.length === 0) return { headers: [], rows: [] };
  const headers = rows[0].map((h) => String(h || "").trim());
  const body = rows.slice(1).filter((r) => r.some((c) => String(c).trim() !== ""));
  const objs = body.map((r) => Object.fromEntries(headers.map((h, idx) => [h, r[idx] ?? ""])));
  return { headers, rows: objs };
}

/** Normalize header names: lowercased, strip non-alnum. */
function normHeader(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/** Parse currency/number-like strings: strip $, commas, spaces, handle (123.45) negatives. */
export function toNumber(v) {
  if (v === null || v === undefined) return 0;
  let s = String(v).trim();
  if (!s) return 0;
  // handle (123.45) as negative
  let neg = false;
  if (s.startsWith("(") && s.endsWith(")")) { neg = true; s = s.slice(1, -1); }
  // strip currency symbols and grouping spaces/commas
  s = s.replace(/[,$\s₵£€₹¥₽₩₺₦]/g, "");
  // some locales export trailing minus: 123.45-
  if (s.endsWith("-")) { neg = true; s = s.slice(0, -1); }
  // if someone used comma as decimal and no dot, convert single comma to dot
  if (!s.includes(".") && (s.match(/,/g) || []).length === 1) {
    // already stripped commas above; noop
  }
  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return neg ? -n : n;
}

/** Build header map: originalHeader -> canonicalName (or null if unknown) */
export function buildHeaderMap(headers = []) {
  const candidates = {
    // dates / period
    paydate: "payDate", date: "payDate", payday: "payDate",
    periodstart: "periodStart", start: "periodStart", payperiodstart: "periodStart",
    periodend: "periodEnd", end: "periodEnd", payperiodend: "periodEnd",

    // amounts (gross/net)
    gross: "gross", grosspay: "gross", grossamount: "gross",
    earnings: "gross", totalearnings: "gross",

    takehome: "takeHome", net: "takeHome", netpay: "takeHome", netamount: "takeHome", netincome: "takeHome",

    // taxes
    federal: "federal", federaltax: "federal", fed: "federal",
    provincial: "provincial", provincetax: "provincial", provincialtax: "provincial", prov: "provincial",
    state: "state", statetax: "state",
    territorial: "territorial", territory: "territorial", territorialtax: "territorial",

    // other deductions
    ei: "ei", employmentinsurance: "ei",
    cpp: "cpp", qpp: "cpp", // treat QPP as cpp bucket for now
    cpp2: "cpp2", qpp2: "cpp2",
    rrsp: "rrsp", rrspdeduction: "rrsp",
    benefits: "benefits", benefit: "benefits",
    benefits: "benefits", 
    misc: "misc", other: "misc", otherdeductions: "misc",
  };

  const map = {};
  for (const h of headers) {
    const n = normHeader(h);
    map[h] = candidates[n] || null;
  }
  return map;
}

/** Pick a value from row using canonical name via the header map. */
function pick(row, map, canonical) {
  const src = Object.keys(map).find((h) => map[h] === canonical);
  return src ? row[src] : "";
}

/** Convert one CSV row into our canonical stub shape with numbers. */
export function normalizeCsvRow(rowObj, map, idx) {
  const taxes = {
    federal: toNumber(pick(rowObj, map, "federal")),
    provincial: toNumber(pick(rowObj, map, "provincial")),
    state: toNumber(pick(rowObj, map, "state")),
    territorial: toNumber(pick(rowObj, map, "territorial")),
  };
  const other = {
    ei: toNumber(pick(rowObj, map, "ei")),
    cpp: toNumber(pick(rowObj, map, "cpp")),
    cpp2: toNumber(pick(rowObj, map, "cpp2")),
    rrsp: toNumber(pick(rowObj, map, "rrsp")),
    benefits: toNumber(pick(rowObj, map, "benefits")),
    misc: toNumber(pick(rowObj, map, "misc")),
  };

  const gross = toNumber(pick(rowObj, map, "gross"));
  const takeHomeRaw = pick(rowObj, map, "takeHome");
  const takeHome = String(takeHomeRaw || "").trim() === "" ? null : toNumber(takeHomeRaw);

  return {
    __rowId: `csv-${idx}`,
    payDate: (pick(rowObj, map, "payDate") || "").trim(),
    periodStart: (pick(rowObj, map, "periodStart") || "").trim() || null,
    periodEnd: (pick(rowObj, map, "periodEnd") || "").trim() || null,
    gross,
    taxes,
    otherDeductions: other,
    takeHome,
  };
}

/** Optional: return headers we couldn't map (for diagnostics) */
export function unmappedHeaders(headers = [], map = {}) {
  const out = [];
  for (const h of headers) if (!map[h]) out.push(h);
  return out;
}
